// Trends — answers "is the operation getting better or worse?"
import { useInfoModal, PageChrome, InfoModal, dqBadgeFor } from './Chrome.jsx';
import { narrativeForTrends, SUB_PAGES } from './helpers.js';
import { BarChart, SmartText, Sparkline } from './Widgets.jsx';

const SERIES = [
  { key: 'in_progress', label: 'Approved/Queued/Synced', color: 'var(--db-green)' },
  { key: 'pending',     label: 'Pending',                color: 'var(--db-orange)' },
  { key: 'gap',         label: 'Gap (blocked)',          color: 'var(--db-red)' },
  { key: 'excluded',    label: 'Skipped by rules',       color: 'var(--db-gray)' },
];

export function PageTrends({ stats, nav }) {
  const info = useInfoModal();
  const helpEntry = SUB_PAGES.find((p) => p.id === 'trends');
  const trend = stats.daily_trend || [];

  return (
    <>
      <PageChrome
        title="Trends"
        subtitle={`Last ${trend.length} days of billing activity.`}
        onHome={() => nav.goSubPage('overview')}
        onBack={nav.goBack} canBack={nav.canBack}
        onInfo={info.onOpen}
        dqBadge={dqBadgeFor(stats, () => nav.goSubPage('data_quality'))}
      />

      <SmartText lines={narrativeForTrends(trend)} />

      <div className="db-chart-card">
        <div className="db-chart-head">
          <div className="db-chart-title">Daily volume by status</div>
          <div className="db-chart-legend">
            {SERIES.map((s) => (
              <span key={s.key}>
                <span className="db-chart-swatch" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>
        <BarChart rows={trend} series={SERIES} xKey="date" yMaxKey="total" />
      </div>

      <div className="db-card">
        <div className="db-card-title">Hours per day</div>
        <div className="db-card-sub">Worked hours, separated by what is billable, blocked, or skipped.</div>
        <BarChart
          rows={trend}
          series={[
            { key: 'pending_hours',  label: 'Pending hrs',  color: 'var(--db-orange)' },
            { key: 'gap_hours',      label: 'Gap hrs',      color: 'var(--db-red)' },
            { key: 'excluded_hours', label: 'Excluded hrs', color: 'var(--db-gray)' },
          ]}
          xKey="date"
          yMaxKey="total_hours"
        />
      </div>

      <div className="db-two-col">
        <SparklineCard title="Pending" values={trend.map((d) => d.pending || 0)} color="var(--db-orange)" />
        <SparklineCard title="Gap"     values={trend.map((d) => d.gap || 0)}     color="var(--db-red)" />
      </div>
      <div className="db-two-col">
        <SparklineCard title="Excluded" values={trend.map((d) => d.excluded || 0)} color="var(--db-gray)" />
        <SparklineCard title="Total volume" values={trend.map((d) => d.total || 0)} color="var(--db-navy)" />
      </div>

      <InfoModal open={info.open} onClose={info.onClose} title="About Trends" body={helpEntry?.help} />
    </>
  );
}

function SparklineCard({ title, values, color }) {
  const last = values[values.length - 1] || 0;
  const prev = values[values.length - 2] || 0;
  const delta = prev > 0 ? ((last - prev) / prev) * 100 : 0;
  return (
    <div className="db-card">
      <div className="db-card-title">{title}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ fontSize: 24, fontWeight: 700 }}>{(last || 0).toLocaleString()}</div>
        {prev > 0 && (
          <div style={{ fontSize: 12, color: delta >= 0 ? 'var(--db-orange)' : 'var(--db-green)' }}>
            {delta >= 0 ? '↑' : '↓'} {Math.abs(delta).toFixed(0)}% day-over-day
          </div>
        )}
      </div>
      <Sparkline values={values} color={color} />
    </div>
  );
}
